import {
  ApiError,
  ERROR_CODES,
  MILLIS_PER_HOUR,
  MILLIS_PER_MINUTE,
  type EpochMillis,
} from '@luacentrix/shared';
import type { SecurityEventSeverity } from '@luacentrix/contracts';
import {
  DEFAULT_NOTIFICATION_POLICY,
  parseNotificationPolicy,
  type NotificationPolicy,
} from './notifications';

export const ENDPOINT_CLASSES = [
  'login',
  'authentication',
  'registration',
  'password_reset',
  'api',
  'license',
  'upload',
  'obfuscator',
  'discord',
  'page',
  'health',
] as const;

export type EndpointClass = (typeof ENDPOINT_CLASSES)[number];

export const ACCESS_TIERS = ['anonymous', 'member', 'premium', 'business', 'admin'] as const;

export type AccessTier = (typeof ACCESS_TIERS)[number];

export const DETECTION_SOURCES = [
  'application',
  'edge',
  'authentication',
  'license',
  'discord',
  'obfuscator',
  'upload',
  'administrator',
  'cloudflare',
] as const;

export type DetectionSource = (typeof DETECTION_SOURCES)[number];

export const DETECTION_KINDS = [
  'rate_limit',
  'auth_failure',
  'authorization_failure',
  'injection',
  'cross_site',
  'path_traversal',
  'upload_anomaly',
  'automation',
  'resource_consumption',
  'endpoint_probe',
  'ip_spoof_attempt',
] as const;

export type DetectionKind = (typeof DETECTION_KINDS)[number];

export const BLOCK_KINDS = ['temporary', 'permanent'] as const;

export type BlockKind = (typeof BLOCK_KINDS)[number];

export interface RateRule {
  requests: number;
  window_ms: number;
}

export interface DetectorConfig {
  enabled: boolean;
  weight: number;
}

export interface EscalationThresholds {
  auth_failures: number;
  authz_failures: number;
  distinct_identities: number;
  probe_paths: number;
  throttle_events: number;
  invalid_jobs: number;
  rejected_uploads: number;
  bytes_in: number;
}

export interface AutoBlockConfig {
  enabled: boolean;
  min_confidence: number;
  min_signals: number;
  shared_address_min_signals: number;
  cooldown_ms: number;
  max_auto_duration_ms: number;
  duration_by_severity: Record<SecurityEventSeverity, number>;
  escalation: EscalationThresholds;
}

export interface BlockPageConfig {
  title: string;
  description: string;
  support_email?: string | undefined;
  support_url: string;
  message_temporary: string;
  message_permanent: string;
}

export interface SecurityPolicy {
  id: string;
  version: number;
  enabled: boolean;
  fail_open: boolean;
  trusted_proxies: string[];
  rate_limits: Record<EndpointClass, Partial<Record<AccessTier, RateRule>>>;
  detectors: Record<DetectionKind, DetectorConfig>;
  auto_block: AutoBlockConfig;
  throttle_retry_after_ms: number;
  block_page: BlockPageConfig;
  notifications: NotificationPolicy;
  updated_at: EpochMillis;
  updated_by: string | null;
}

const RATE_LIMITS: Record<EndpointClass, Partial<Record<AccessTier, RateRule>>> = {
  login: {
    anonymous: { requests: 5, window_ms: 15 * MILLIS_PER_MINUTE },
    member: { requests: 12, window_ms: 15 * MILLIS_PER_MINUTE },
  },
  authentication: {
    anonymous: { requests: 20, window_ms: 15 * MILLIS_PER_MINUTE },
    member: { requests: 40, window_ms: 15 * MILLIS_PER_MINUTE },
    premium: { requests: 60, window_ms: 15 * MILLIS_PER_MINUTE },
    business: { requests: 80, window_ms: 15 * MILLIS_PER_MINUTE },
    admin: { requests: 120, window_ms: 15 * MILLIS_PER_MINUTE },
  },
  registration: {
    anonymous: { requests: 3, window_ms: MILLIS_PER_HOUR },
  },
  password_reset: {
    anonymous: { requests: 5, window_ms: MILLIS_PER_HOUR },
    member: { requests: 10, window_ms: MILLIS_PER_HOUR },
  },
  api: {
    anonymous: { requests: 60, window_ms: MILLIS_PER_MINUTE },
    member: { requests: 300, window_ms: MILLIS_PER_MINUTE },
    premium: { requests: 600, window_ms: MILLIS_PER_MINUTE },
    business: { requests: 1200, window_ms: MILLIS_PER_MINUTE },
    admin: { requests: 1800, window_ms: MILLIS_PER_MINUTE },
  },
  license: {
    anonymous: { requests: 60, window_ms: MILLIS_PER_MINUTE },
    member: { requests: 120, window_ms: MILLIS_PER_MINUTE },
    premium: { requests: 300, window_ms: MILLIS_PER_MINUTE },
    business: { requests: 600, window_ms: MILLIS_PER_MINUTE },
  },
  upload: {
    member: { requests: 20, window_ms: MILLIS_PER_HOUR },
    premium: { requests: 60, window_ms: MILLIS_PER_HOUR },
    business: { requests: 200, window_ms: MILLIS_PER_HOUR },
  },
  obfuscator: {
    member: { requests: 10, window_ms: MILLIS_PER_HOUR },
    premium: { requests: 40, window_ms: MILLIS_PER_HOUR },
    business: { requests: 120, window_ms: MILLIS_PER_HOUR },
  },
  discord: {
    anonymous: { requests: 120, window_ms: MILLIS_PER_MINUTE },
  },
  page: {
    anonymous: { requests: 600, window_ms: MILLIS_PER_MINUTE },
    member: { requests: 900, window_ms: MILLIS_PER_MINUTE },
  },
  health: {
    anonymous: { requests: 60, window_ms: MILLIS_PER_MINUTE },
  },
};

const DETECTOR_WEIGHTS: Record<DetectionKind, number> = {
  rate_limit: 0.45,
  auth_failure: 0.35,
  authorization_failure: 0.25,
  injection: 0.55,
  cross_site: 0.5,
  path_traversal: 0.45,
  upload_anomaly: 0.35,
  automation: 0.2,
  resource_consumption: 0.3,
  endpoint_probe: 0.25,
  ip_spoof_attempt: 0.15,
};

export const MINIMUM_AUTO_BLOCK_CONFIDENCE = 0.7;

export const DEFAULT_SECURITY_POLICY: SecurityPolicy = {
  id: 'spl_default',
  version: 1,
  enabled: true,
  fail_open: true,
  trusted_proxies: [],
  rate_limits: RATE_LIMITS,
  detectors: DETECTION_KINDS.reduce(
    (accumulated, kind) => {
      accumulated[kind] = { enabled: true, weight: DETECTOR_WEIGHTS[kind] };
      return accumulated;
    },
    {} as Record<DetectionKind, DetectorConfig>,
  ),
  auto_block: {
    enabled: true,
    min_confidence: 0.75,
    min_signals: 2,
    shared_address_min_signals: 3,
    cooldown_ms: 6 * MILLIS_PER_HOUR,
    max_auto_duration_ms: 24 * MILLIS_PER_HOUR,
    duration_by_severity: {
      low: 15 * MILLIS_PER_MINUTE,
      medium: MILLIS_PER_HOUR,
      high: 6 * MILLIS_PER_HOUR,
      critical: 24 * MILLIS_PER_HOUR,
    },
    escalation: {
      auth_failures: 10,
      authz_failures: 15,
      distinct_identities: 8,
      probe_paths: 12,
      throttle_events: 5,
      invalid_jobs: 6,
      rejected_uploads: 4,
      bytes_in: 8 * 1024 * 1024,
    },
  },
  throttle_retry_after_ms: MILLIS_PER_MINUTE,
  block_page: {
    title: 'Access restricted',
    description:
      'Automated protection has temporarily restricted this connection. If this is a mistake, post the reference below in the official Centrix Hub Discord server.',
    support_url: 'https://discord.gg/v4MnRRwrV7',
    message_temporary: 'This restriction expires automatically.',
    message_permanent: 'This restriction is under administrator review.',
  },
  notifications: DEFAULT_NOTIFICATION_POLICY,
  updated_at: 0,
  updated_by: null,
};

const TIER_FALLBACK: Record<AccessTier, readonly AccessTier[]> = {
  anonymous: ['anonymous'],
  member: ['member', 'anonymous'],
  premium: ['premium', 'member', 'anonymous'],
  business: ['business', 'premium', 'member', 'anonymous'],
  admin: ['admin', 'business', 'premium', 'member', 'anonymous'],
};

export function clonePolicy(policy: SecurityPolicy): SecurityPolicy {
  return {
    ...policy,
    trusted_proxies: [...policy.trusted_proxies],
    rate_limits: cloneRateLimits(policy.rate_limits),
    detectors: cloneDetectors(policy.detectors),
    auto_block: {
      ...policy.auto_block,
      duration_by_severity: { ...policy.auto_block.duration_by_severity },
    },
    block_page: { ...policy.block_page },
    notifications: parseNotificationPolicy(policy.notifications ?? null),
  };
}

function cloneRateLimits(
  limits: Record<EndpointClass, Partial<Record<AccessTier, RateRule>>>,
): Record<EndpointClass, Partial<Record<AccessTier, RateRule>>> {
  const output = {} as Record<EndpointClass, Partial<Record<AccessTier, RateRule>>>;
  for (const endpoint of ENDPOINT_CLASSES) {
    const tiered: Partial<Record<AccessTier, RateRule>> = {};
    for (const tier of ACCESS_TIERS) {
      const rule = limits[endpoint]?.[tier];
      if (rule !== undefined) {
        tiered[tier] = { requests: rule.requests, window_ms: rule.window_ms };
      }
    }
    output[endpoint] = tiered;
  }
  return output;
}

function cloneDetectors(
  detectors: Record<DetectionKind, DetectorConfig>,
): Record<DetectionKind, DetectorConfig> {
  const output = {} as Record<DetectionKind, DetectorConfig>;
  for (const kind of DETECTION_KINDS) {
    const config = detectors[kind];
    output[kind] = {
      enabled: config?.enabled ?? true,
      weight: config?.weight ?? DETECTOR_WEIGHTS[kind],
    };
  }
  return output;
}

export function resolveRateRule(
  policy: SecurityPolicy,
  endpoint: EndpointClass,
  tier: AccessTier,
): RateRule | null {
  const table = policy.rate_limits[endpoint];
  for (const candidate of TIER_FALLBACK[tier]) {
    const rule = table?.[candidate];
    if (rule !== undefined) {
      return { requests: rule.requests, window_ms: rule.window_ms };
    }
  }
  return null;
}

export function detectorEnabled(policy: SecurityPolicy, kind: DetectionKind): boolean {
  return policy.enabled && (policy.detectors[kind]?.enabled ?? false);
}

export function detectorWeight(policy: SecurityPolicy, kind: DetectionKind): number {
  return policy.detectors[kind]?.weight ?? 0;
}

export function blockDurationFor(policy: SecurityPolicy, severity: SecurityEventSeverity): number {
  const configured = policy.auto_block.duration_by_severity[severity] ?? MILLIS_PER_MINUTE;
  return Math.min(configured, policy.auto_block.max_auto_duration_ms);
}

function fail(field: string, reason: string): never {
  throw new ApiError(ERROR_CODES.VALIDATION_FAILED, { details: { field, reason } });
}

function readNumber(input: Record<string, unknown>, field: string, minimum: number): number {
  const value = input[field];
  if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum) {
    fail(field, 'number_minimum');
  }
  return value;
}

function readBoolean(input: Record<string, unknown>, field: string): boolean {
  const value = input[field];
  if (typeof value !== 'boolean') {
    fail(field, 'boolean_required');
  }
  return value;
}

function readString(input: Record<string, unknown>, field: string, maxLength: number): string {
  const value = input[field];
  if (typeof value !== 'string' || value.trim() === '' || value.length > maxLength) {
    fail(field, 'string_required');
  }
  return value.trim();
}

function readOptionalEmail(input: Record<string, unknown>, field: string): string | undefined {
  const raw = input[field];
  if (raw === undefined || raw === null) {
    return undefined;
  }
  if (typeof raw !== 'string') {
    fail(field, 'email_required');
  }
  const value = raw.trim().toLowerCase();
  if (value === '') {
    return undefined;
  }
  if (!/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(value)) {
    fail(field, 'email_required');
  }
  return value;
}

function readHttpsUrl(input: Record<string, unknown>, field: string): string {
  const value = readString(input, field, 512);
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return fail(field, 'absolute_https_url');
  }
  if (parsed.protocol !== 'https:') {
    fail(field, 'absolute_https_url');
  }
  return parsed.toString();
}

function parseRateLimits(input: unknown): SecurityPolicy['rate_limits'] {
  if (typeof input !== 'object' || input === null) {
    fail('rate_limits', 'object_required');
  }
  const source = input as Record<string, unknown>;
  const output = {} as SecurityPolicy['rate_limits'];
  for (const endpoint of ENDPOINT_CLASSES) {
    const tiered: Partial<Record<AccessTier, RateRule>> = {};
    const raw = source[endpoint];
    if (raw !== undefined) {
      if (typeof raw !== 'object' || raw === null) {
        fail(`rate_limits.${endpoint}`, 'object_required');
      }
      const table = raw as Record<string, unknown>;
      for (const tier of ACCESS_TIERS) {
        const rule = table[tier];
        if (rule === undefined) {
          continue;
        }
        if (typeof rule !== 'object' || rule === null) {
          fail(`rate_limits.${endpoint}.${tier}`, 'object_required');
        }
        const record = rule as Record<string, unknown>;
        tiered[tier] = {
          requests: readNumber(record, 'requests', 1),
          window_ms: readNumber(record, 'window_ms', MILLIS_PER_MINUTE),
        };
      }
    } else {
      const fallback = DEFAULT_SECURITY_POLICY.rate_limits[endpoint];
      for (const tier of ACCESS_TIERS) {
        const rule = fallback[tier];
        if (rule !== undefined) {
          tiered[tier] = { requests: rule.requests, window_ms: rule.window_ms };
        }
      }
    }
    output[endpoint] = tiered;
  }
  return output;
}

function parseDetectors(input: unknown): Record<DetectionKind, DetectorConfig> {
  const output = {} as Record<DetectionKind, DetectorConfig>;
  const source =
    typeof input === 'object' && input !== null ? (input as Record<string, unknown>) : {};
  for (const kind of DETECTION_KINDS) {
    const raw = source[kind];
    if (raw === undefined) {
      output[kind] = { enabled: true, weight: DETECTOR_WEIGHTS[kind] };
      continue;
    }
    if (typeof raw !== 'object' || raw === null) {
      fail(`detectors.${kind}`, 'object_required');
    }
    const record = raw as Record<string, unknown>;
    const weight = readNumber(record, 'weight', 0);
    if (weight > 1) {
      fail(`detectors.${kind}.weight`, 'number_maximum');
    }
    output[kind] = { enabled: readBoolean(record, 'enabled'), weight };
  }
  return output;
}

function parseAutoBlock(input: unknown): AutoBlockConfig {
  if (typeof input !== 'object' || input === null) {
    fail('auto_block', 'object_required');
  }
  const record = input as Record<string, unknown>;
  const durations = record['duration_by_severity'];
  if (typeof durations !== 'object' || durations === null) {
    fail('auto_block.duration_by_severity', 'object_required');
  }
  const durationRecord = durations as Record<string, unknown>;
  const duration_by_severity = {} as Record<SecurityEventSeverity, number>;
  for (const severity of ['low', 'medium', 'high', 'critical'] as const) {
    duration_by_severity[severity] = readNumber(durationRecord, severity, MILLIS_PER_MINUTE);
  }
  const confidence = readNumber(record, 'min_confidence', 0);
  if (confidence < MINIMUM_AUTO_BLOCK_CONFIDENCE) {
    fail('auto_block.min_confidence', 'confidence_too_low');
  }
  if (readNumber(record, 'min_signals', 1) < 2) {
    fail('auto_block.min_signals', 'single_signal_blocks_forbidden');
  }
  const maxDuration = readNumber(record, 'max_auto_duration_ms', MILLIS_PER_MINUTE);
  const escalationSource =
    typeof record['escalation'] === 'object' && record['escalation'] !== null
      ? (record['escalation'] as Record<string, unknown>)
      : {};
  const escalation: EscalationThresholds = {
    auth_failures: readNumber(escalationSource, 'auth_failures', 2),
    authz_failures: readNumber(escalationSource, 'authz_failures', 2),
    distinct_identities: readNumber(escalationSource, 'distinct_identities', 2),
    probe_paths: readNumber(escalationSource, 'probe_paths', 2),
    throttle_events: readNumber(escalationSource, 'throttle_events', 2),
    invalid_jobs: readNumber(escalationSource, 'invalid_jobs', 2),
    rejected_uploads: readNumber(escalationSource, 'rejected_uploads', 2),
    bytes_in: readNumber(escalationSource, 'bytes_in', 1),
  };
  return {
    enabled: readBoolean(record, 'enabled'),
    min_confidence: confidence,
    min_signals: readNumber(record, 'min_signals', 2),
    shared_address_min_signals: readNumber(record, 'shared_address_min_signals', 2),
    cooldown_ms: readNumber(record, 'cooldown_ms', MILLIS_PER_MINUTE),
    max_auto_duration_ms: maxDuration,
    duration_by_severity,
    escalation,
  };
}

function parseBlockPage(input: unknown): BlockPageConfig {
  if (typeof input !== 'object' || input === null) {
    fail('block_page', 'object_required');
  }
  const record = input as Record<string, unknown>;
  const email = readOptionalEmail(record, 'support_email');
  return {
    title: readString(record, 'title', 120),
    description: readString(record, 'description', 600),
    ...(email === undefined ? {} : { support_email: email }),
    support_url: readHttpsUrl(record, 'support_url'),
    message_temporary: readString(record, 'message_temporary', 400),
    message_permanent: readString(record, 'message_permanent', 400),
  };
}

export function parsePolicy(input: unknown): SecurityPolicy {
  if (typeof input !== 'object' || input === null) {
    fail('policy', 'object_required');
  }
  const record = input as Record<string, unknown>;
  const proxies = record['trusted_proxies'];
  if (proxies !== undefined && !Array.isArray(proxies)) {
    fail('trusted_proxies', 'array_required');
  }
  const updatedBy = record['updated_by'];
  return {
    id: readString(record, 'id', 64),
    version: readNumber(record, 'version', 1),
    enabled: readBoolean(record, 'enabled'),
    fail_open: readBoolean(record, 'fail_open'),
    trusted_proxies: Array.isArray(proxies)
      ? proxies.map(entry => readString({ entry } as Record<string, unknown>, 'entry', 64))
      : [],
    rate_limits: parseRateLimits(record['rate_limits']),
    detectors: parseDetectors(record['detectors']),
    auto_block: parseAutoBlock(record['auto_block']),
    throttle_retry_after_ms: readNumber(record, 'throttle_retry_after_ms', 1),
    block_page: parseBlockPage(record['block_page']),
    notifications: parseNotificationPolicy(record['notifications'] ?? null),
    updated_at: readNumber(record, 'updated_at', 0),
    updated_by: typeof updatedBy === 'string' ? updatedBy : null,
  };
}
